import { useTranslation } from "react-i18next";
import { Link, NavLink } from "react-router";

import { canAccess } from "@/auth/access";
import { useSession } from "@/auth/session-context";
import { Brand } from "@/components/brand";
import { paths } from "@/routes/paths";
import { cn } from "@punto-firma/ui/lib/utils";

import { navSections } from "./navigation";

/** Navegación filtrada por el acceso de la sesión actual. */
const useVisibleSections = () => {
  const session = useSession();
  return navSections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => canAccess(session, item.access)),
    }))
    .filter((section) => section.items.length > 0);
};

export const SidebarNav = ({ onNavigate }: { onNavigate?: () => void }) => {
  const { t } = useTranslation("nav");
  const sections = useVisibleSections();

  return (
    <nav className="flex flex-col gap-6" aria-label={t("ariaLabel")}>
      {sections.map((section, index) => (
        <div key={section.titleKey ?? index} className="flex flex-col gap-1">
          {section.titleKey ? (
            <p className="px-3 pb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {t(section.titleKey)}
            </p>
          ) : null}
          {section.items.map((item) => (
            <NavLink
              key={item.href}
              to={item.href}
              end={item.end}
              onClick={onNavigate}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
                  isActive
                    ? "bg-accent font-medium text-accent-foreground"
                    : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
                )
              }
            >
              <item.icon className="size-4 shrink-0" aria-hidden />
              {t(item.labelKey)}
            </NavLink>
          ))}
        </div>
      ))}
    </nav>
  );
};

/** Logo de Punto.Firma, que lleva a Inicio. */
export const SidebarBrand = ({ onNavigate }: { onNavigate?: () => void }) => (
  <Link
    to={paths.home}
    onClick={onNavigate}
    className="flex h-9 items-center px-3"
  >
    <Brand className="h-5" />
  </Link>
);

/**
 * Barra lateral fija en escritorio: el logo y la navegación. En móvil, lo
 * mismo va en un `Sheet` (cabecera). La organización se cambia en el menú de
 * usuario.
 */
export const Sidebar = () => (
  <aside className="sticky top-0 hidden h-screen w-64 shrink-0 flex-col gap-6 border-r bg-background px-3 py-4 md:flex">
    <SidebarBrand />
    <SidebarNav />
  </aside>
);
